import { useRef } from 'react';

import type { SelectOptionsSource } from '../config';
import type { SelectOption } from '../lease-details.types';

type ResolvedSelectOptions<T extends SelectOption> = {
  options: T[];
  isLoading: boolean;
  isError: boolean;
};

/**
 * Resolves a `SelectOptionsSource` (static or hook-backed) into a uniform
 * shape so the renderer treats both the same way. The source variant must be
 * stable across renders (enforced by the invariant below) — under that
 * contract the hook below is unconditional in lexical position, satisfying
 * React's rules of hooks. For static sources we synthesize a trivial no-op
 * "hook" so the call site stays unconditional.
 */
export const useResolvedOptions = <T extends SelectOption>(
  source: SelectOptionsSource<T>,
): ResolvedSelectOptions<T> => {
  const firstKind = useRef(source.kind);
  if (firstKind.current !== source.kind) {
    throw new Error(
      `useResolvedOptions: source.kind changed from "${firstKind.current}" to "${source.kind}" between renders. The variant must be stable for the lifetime of the field.`,
    );
  }

  const useOptions: () => ResolvedSelectOptions<T> =
    source.kind === 'hook'
      ? source.useOptions
      : () => ({ options: source.options, isLoading: false, isError: false });

  return useOptions();
};
